import { GCC_STUDIO_API_BASE_URL } from '../runtimeConfig';
import { ALPHASENSE_FEED_KEYWORD, ALPHASENSE_FEED_PROMPT } from '../constants/alphasense';
import { encryptPayload } from '../utils/cryptoClient';

function buildHeaders(msalToken) {
  const headers = { 'Content-Type': 'application/json' };
  if (msalToken) headers['Authorization'] = `Bearer ${msalToken}`;
  return headers;
}

/**
 * POST /alphasense/documents/search
 *
 * @param {string} keyword
 * @param {string} msalToken      - MSAL access token (Authorization header to proxy)
 * @param {string} alphasenseToken - AlphaSense JWT from AlphaSenseTokenContext
 */
export const searchAlphaSenseDocuments = async (keyword = ALPHASENSE_FEED_KEYWORD, msalToken, alphasenseToken) => {
  const payload = {
    company_tickers: [],
    date_preset: 'LAST_90_DAYS',
    keyword,
    limit: 13,
    sorting_direction: 'DESC',
    sorting_field: 'DATE',
    source_type_ids: [],
    token: alphasenseToken,
  };

  // Hybrid-encrypt the payload: body carries Base64(AES-GCM ciphertext),
  // the RSA-wrapped AES key/IV travel in the x-cryptokey-details header.
  const { value, cryptoKeyDetails } = await encryptPayload(payload);

  const response = await fetch(`${GCC_STUDIO_API_BASE_URL}/alphasense/documents/search`, {
    method: 'POST',
    headers: { ...buildHeaders(msalToken), 'x-cryptokey-details': cryptoKeyDetails },
    body: JSON.stringify({ value }),
  });

  await throwIfNotOk(response, 'AlphaSense search failed');
  return response.json();
};

/**
 * POST /gensearch/list
 *
 * @param {string} msalToken
 * @param {string} alphasenseToken
 */
export const listGensearchAgents = async (msalToken, alphasenseToken) => {
  const { value, cryptoKeyDetails } = await encryptPayload({ limit: 100, offset: 0, workflowType: 'SYSTEM' });
  const response = await fetch(`${GCC_STUDIO_API_BASE_URL}/gensearch/list`, {
    method: 'POST',
    headers: { ...buildHeaders(alphasenseToken), 'x-cryptokey-details': cryptoKeyDetails },
    body: JSON.stringify({ value }),
  });

  await throwIfNotOk(response, 'GenSearch list failed');
  return response.json();
};

function findCreditLimitExt(node) {
  if (node == null) return null;
  // The API can serialize upstream errors into a string (e.g. FastAPI
  // `detail: str(exc)`), so the CREDIT_LIMIT_EXHAUSTED code and retry hint
  // arrive embedded in a Python-repr string rather than a structured object.
  if (typeof node === 'string') {
    if (!node.includes('CREDIT_LIMIT_EXHAUSTED')) return null;
    const match = node.match(/retryAfterSeconds['"]?\s*[:=]\s*(\d+)/);
    return {
      code: 'CREDIT_LIMIT_EXHAUSTED',
      retryAfterSeconds: match ? Number(match[1]) : null,
    };
  }
  if (typeof node !== 'object') return null;
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = findCreditLimitExt(item);
      if (found) return found;
    }
    return null;
  }
  if (node.extensions?.code === 'CREDIT_LIMIT_EXHAUSTED') return node.extensions;
  for (const val of Object.values(node)) {
    const found = findCreditLimitExt(val);
    if (found) return found;
  }
  return null;
}

export const ALPHASENSE_FORBIDDEN_MESSAGE = 'You are not authorized to access AlphaSense';

function throwApiError(message, data, status) {
  let code = null;
  let retryAfterSeconds = null;

  if (status === 403) {
    // Any AlphaSense API returning 403 means the caller is not authorized.
    code = 'FORBIDDEN';
    message = ALPHASENSE_FORBIDDEN_MESSAGE;
  } else {
    const ext = findCreditLimitExt(data);
    if (ext?.code === 'CREDIT_LIMIT_EXHAUSTED') {
      code = 'CREDIT_LIMIT_EXHAUSTED';
      retryAfterSeconds = ext.retryAfterSeconds ?? null;
      message = retryAfterSeconds != null
        ? `Credit limit exceeded. Please try again in ${retryAfterSeconds} seconds.`
        : 'Credit limit exceeded. Please try again shortly.';
    }
  }

  const err = new Error(message);
  err.code = code;
  err.status = status;
  err.retryAfterSeconds = retryAfterSeconds;
  throw err;
}

async function throwIfNotOk(response, fallbackMessage) {
  if (response.ok) return;
  let data = null;
  try { data = await response.json(); } catch { /* non-JSON body */ }
  throwApiError(`${fallbackMessage}: ${response.status}`, data, response.status);
}

/**
 * POST /gensearch/workflow-agents/run
 *
 * @param {string} agentId
 * @param {string} msalToken
 * @param {string} alphasenseToken
 */
export const runWorkflowAgent = async (agentId, msalToken, alphasenseToken) => {
  const { value, cryptoKeyDetails } = await encryptPayload({ agentId });
  const response = await fetch(`${GCC_STUDIO_API_BASE_URL}/gensearch/workflow-agents/run`, {
    method: 'POST',
    headers: { ...buildHeaders(alphasenseToken), 'x-cryptokey-details': cryptoKeyDetails },
    body: JSON.stringify({ value }),
  });

  await throwIfNotOk(response, 'Workflow agent run failed');
  return response.json();
};

/**
 * POST /gensearch/deep-research
 *
 * @param {string} prompt        - company name / query from the search
 * @param {string} conversationId - returned by runWorkflowAgent
 * @param {string} msalToken
 * @param {string} alphasenseToken
 */
export const deepResearch = async (prompt, conversationId, msalToken, alphasenseToken) => {
  const { value, cryptoKeyDetails } = await encryptPayload({ prompt, conversationId });
  const response = await fetch(`${GCC_STUDIO_API_BASE_URL}/gensearch/deep-research`, {
    method: 'POST',
    headers: { ...buildHeaders(alphasenseToken), 'x-cryptokey-details': cryptoKeyDetails },
    body: JSON.stringify({ value }),
  });

  await throwIfNotOk(response, 'Deep research failed');
  return response.json();
};

/**
 * POST /gensearch/auto
 *
 * @param {string} prompt
 * @param {string} msalToken
 * @param {string} alphasenseToken
 */
/**
 * POST /gensearch/insights
 * Returns structured GCC intelligence headlines using the feed prompt.
 *
 * @param {string} msalToken
 * @param {string} alphasenseToken
 */
export const gensearchInsights = async (msalToken, alphasenseToken) => {
  const { value, cryptoKeyDetails } = await encryptPayload({ prompt: ALPHASENSE_FEED_PROMPT });
  const response = await fetch(`${GCC_STUDIO_API_BASE_URL}/gensearch/insights`, {
    method: 'POST',
    headers: { ...buildHeaders(alphasenseToken), 'x-cryptokey-details': cryptoKeyDetails },
    body: JSON.stringify({ value }),
  });

  await throwIfNotOk(response, 'GenSearch insights failed');
  return response.json();
};

/**
 * POST /gensearch/auto
 *
 * @param {string} prompt
 * @param {string} msalToken
 * @param {string} alphasenseToken
 */
export const gensearchAuto = async (prompt, msalToken, alphasenseToken) => {
  // Hybrid-encrypt the payload: body carries Base64(AES-GCM ciphertext),
  // the RSA-wrapped AES key/IV travel in the x-cryptokey-details header.
  // The Authorization bearer (alphasenseToken) stays plaintext.
  const { value, cryptoKeyDetails } = await encryptPayload({ prompt });

  const response = await fetch(`${GCC_STUDIO_API_BASE_URL}/gensearch/auto`, {
    method: 'POST',
    headers: { ...buildHeaders(alphasenseToken), 'x-cryptokey-details': cryptoKeyDetails },
    body: JSON.stringify({ value }),
  });

  await throwIfNotOk(response, 'GenSearch failed');
  return response.json();
};
